import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { bind } from 'bind-event-listener';
import noop from '@atlaskit/ds-lib/noop';
import { COLOR_MODE_ATTRIBUTE } from './constants';
import getThemeHtmlAttrs from './get-theme-html-attrs';
import { themeStateDefaults } from './theme-config';
import { isValidBrandHex } from './utils/color-utils';
import { findMissingCustomStyleElements } from './utils/custom-theme-loading-utils';
import { getThemePreferences } from './utils/get-theme-preferences';
import { darkModeMediaQuery, loadAndAppendThemeCss } from './utils/theme-loading';

// Represents theme state once mounted to the page (auto is hidden from observers)

var isMatchMediaAvailable = typeof window !== 'undefined' && 'matchMedia' in window;
var darkModeMql = isMatchMediaAvailable && window.matchMedia(darkModeMediaQuery);
var unbindThemeChangeListener = noop;

/**
 * Updates the current theme when the system theme changes. Should be bound
 * to an event listener listening on the '(prefers-color-scheme: dark)' query
 * @param e The event representing a change in system theme.
 */
var checkNativeListener = function checkNativeListener(e) {
  var element = document.documentElement;
  element.setAttribute(COLOR_MODE_ATTRIBUTE, e.matches ? 'dark' : 'light');
};

/**
 * Sets the theme globally at runtime. This updates the `data-theme` and `data-color-mode` attributes on your page's <html> tag.
 *
 * @param {Object<string, string>} themeState The themes and color mode that should be applied.
 * @param {string} themeState.colorMode Determines which color theme is applied. If set to `auto`, the theme applied will be determined by the OS setting.
 * @param {string} themeState.dark The color theme to be applied when the color mode resolves to 'dark'.
 * @param {string} themeState.light The color theme to be applied when the color mode resolves to 'light'.
 * @param {string} themeState.shape The shape theme to be applied.
 * @param {string} themeState.spacing The spacing theme to be applied.
 * @param {string} themeState.typography The typography theme to be applied.
 * @param {Object} themeState.UNSAFE_themeOptions The custom branding options to be used for custom theme generation
 * @param {function} themeLoader Callback function used to override the default theme loading functionality.
 *
 * @returns A Promise of an unbind function, that can be used to stop listening for changes to system theme.
 *
 * @example
 * ```
 * setGlobalTheme({colorMode: 'auto', light: 'light', dark: 'dark', spacing: 'spacing'});
 * ```
 */
var setGlobalTheme = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    var _ref2,
      _ref2$colorMode,
      colorMode,
      _ref2$dark,
      dark,
      _ref2$light,
      light,
      _ref2$shape,
      shape,
      _ref2$spacing,
      spacing,
      _ref2$typography,
      typography,
      _ref2$UNSAFE_themeOpt,
      UNSAFE_themeOptions,
      themeLoader,
      themePreferences,
      loadingStrategy,
      themeAttributes,
      _args3 = arguments;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          _ref2 = _args3.length > 0 && _args3[0] !== undefined ? _args3[0] : {}, _ref2$colorMode = _ref2.colorMode, colorMode = _ref2$colorMode === void 0 ? themeStateDefaults['colorMode'] : _ref2$colorMode, _ref2$dark = _ref2.dark, dark = _ref2$dark === void 0 ? themeStateDefaults['dark'] : _ref2$dark, _ref2$light = _ref2.light, light = _ref2$light === void 0 ? themeStateDefaults['light'] : _ref2$light, _ref2$shape = _ref2.shape, shape = _ref2$shape === void 0 ? themeStateDefaults['shape'] : _ref2$shape, _ref2$spacing = _ref2.spacing, spacing = _ref2$spacing === void 0 ? themeStateDefaults['spacing'] : _ref2$spacing, _ref2$typography = _ref2.typography, typography = _ref2$typography === void 0 ? themeStateDefaults['typography'] : _ref2$typography, _ref2$UNSAFE_themeOpt = _ref2.UNSAFE_themeOptions, UNSAFE_themeOptions = _ref2$UNSAFE_themeOpt === void 0 ? themeStateDefaults['UNSAFE_themeOptions'] : _ref2$UNSAFE_themeOpt;
          themeLoader = _args3.length > 1 ? _args3[1] : undefined;
          themePreferences = getThemePreferences({
            colorMode: colorMode,
            dark: dark,
            light: light,
            shape: shape,
            spacing: spacing,
            typography: typography
          });
          loadingStrategy = themeLoader ? themeLoader : loadAndAppendThemeCss;
          _context3.next = 6;
          return Promise.all([].concat(_toConsumableArray(themePreferences.map( /*#__PURE__*/function () {
            var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(themeId) {
              return _regeneratorRuntime.wrap(function _callee$(_context) {
                while (1) switch (_context.prev = _context.next) {
                  case 0:
                    _context.next = 2;
                    return loadingStrategy(themeId);
                  case 2:
                    return _context.abrupt("return", _context.sent);
                  case 3:
                  case "end":
                    return _context.stop();
                }
              }, _callee);
            }));
            return function (_x) {
              return _ref3.apply(this, arguments);
            };
          }())), [_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
            var mode, attrOfMissingCustomStyles, _yield$import, loadAndAppendCustomThemeCss;
            return _regeneratorRuntime.wrap(function _callee2$(_context2) {
              while (1) switch (_context2.prev = _context2.next) {
                case 0:
                  if (!(!themeLoader && UNSAFE_themeOptions && isValidBrandHex(UNSAFE_themeOptions === null || UNSAFE_themeOptions === void 0 ? void 0 : UNSAFE_themeOptions.brandColor))) {
                    _context2.next = 11;
                    break;
                  }
                  mode = colorMode || themeStateDefaults['colorMode'];
                  attrOfMissingCustomStyles = findMissingCustomStyleElements(UNSAFE_themeOptions, mode);
                  if (!(attrOfMissingCustomStyles.length === 0)) {
                    _context2.next = 5;
                    break;
                  }
                  return _context2.abrupt("return", false);
                case 5:
                  _context2.next = 7;
                  return import( /* webpackChunkName: "@atlaskit-internal_atlassian-custom-theme" */
                  './custom-theme');
                case 7:
                  _yield$import = _context2.sent;
                  loadAndAppendCustomThemeCss = _yield$import.loadAndAppendCustomThemeCss;
                  _context2.next = 11;
                  return loadAndAppendCustomThemeCss({
                    colorMode: attrOfMissingCustomStyles.length === 2 ? 'auto' :
                    // only load the missing custom theme styles
                    attrOfMissingCustomStyles[0],
                    UNSAFE_themeOptions: UNSAFE_themeOptions
                  });
                case 11:
                case "end":
                  return _context2.stop();
              }
            }, _callee2);
          }))()]));
        case 6:
          if (colorMode === 'auto' && darkModeMql) {
            colorMode = darkModeMql.matches ? 'dark' : 'light';
            // Add an event listener for changes to the system theme.
            // If the function exists, it will not be added again.
            unbindThemeChangeListener = bind(darkModeMql, {
              type: 'change',
              listener: checkNativeListener
            });
          } else {
            unbindThemeChangeListener();
          }
          themeAttributes = getThemeHtmlAttrs({
            colorMode: colorMode,
            dark: dark,
            light: light,
            shape: shape,
            spacing: spacing,
            typography: typography,
            UNSAFE_themeOptions: themeLoader ? undefined : UNSAFE_themeOptions
          });
          Object.entries(themeAttributes).forEach(function (_ref5) {
            var _ref6 = _slicedToArray(_ref5, 2),
              key = _ref6[0],
              value = _ref6[1];
            document.documentElement.setAttribute(key, value);
          });
          return _context3.abrupt("return", unbindThemeChangeListener);
        case 10:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return function setGlobalTheme() {
    return _ref.apply(this, arguments);
  };
}();
export default setGlobalTheme;